import createReadonlySet from '@studio/utils/createReadonlySet';

import type { ContentPromotionFeature } from './content-promotion/contentPromotionfeatures';
import type { CurationPromotionFeature } from './curation-promotion/curationPromotionFeatures';
import { SonicFeature } from './features';
import RuleEngine, { type Evaluator } from './RuleEngine';
import rules from './rules';
import { type AccessContext, type AccessFn, and } from './utils';

export * from './content-promotion/contentPromotionfeatures';
export * from './curation-promotion/curationPromotionFeatures';
export * from './features';
export * from './roles';
export * from './utils';

export const ruleEngine = new RuleEngine<AccessContext>(rules);

export const ruleEvaluators = Object.fromEntries(
  ruleEngine.getRuleNames().map(ruleName => {
    const rule = ruleEngine.getRuleOrThrow(ruleName);
    const evaluate = ruleEngine.createEvaluator(rule);
    return [
      ruleName,
      // Wraps it with a dynamically named function
      { [ruleName]: (ctx: Partial<AccessContext>) => evaluate(ctx) }[ruleName]!,
    ];
  }),
) as Readonly<Record<keyof typeof rules, Evaluator<AccessContext>>>;

const CUSTOMER_SUPPORT_EDITABLE_FIELDS = createReadonlySet([
  'username',
  'deleted',
  // 'firstName', // <-- editable in backend (needed for VIP create), but keep readonly in user view
  // 'lastName',  // <-- editable in backend (needed for VIP create), but keep readonly in user view
]);

const USERS_ADMIN_DENIED_FIELDS = createReadonlySet(['roles', 'mfaEnabled']);

export function canEditUserAttribute(attr: string): AccessFn {
  const { isSuperAdmin, isUsersAdmin, isCustomerSupport } = ruleEvaluators;
  return ctx =>
    isSuperAdmin(ctx) ||
    (isUsersAdmin(ctx) && !USERS_ADMIN_DENIED_FIELDS.has(attr)) ||
    (isCustomerSupport(ctx) && CUSTOMER_SUPPORT_EDITABLE_FIELDS.has(attr));
}

export type SonicAppFeatures =
  | SonicFeature
  | ContentPromotionFeature
  | CurationPromotionFeature;

export function isFeatureEnabled(feature: SonicAppFeatures): AccessFn {
  return ruleEvaluators[feature];
}

/** @deprecated DO NOT USE THIS */
export const usesAllPromotedFeatures = and(
  isFeatureEnabled(SonicFeature.IMSForContentRatings),
  isFeatureEnabled(SonicFeature.IMSForContentDescriptors),
  isFeatureEnabled(SonicFeature.IMSForGenres),
  isFeatureEnabled(SonicFeature.IMSForTaxonomyNodes),
  isFeatureEnabled(SonicFeature.ListableAndPlayableTerritories),
  isFeatureEnabled(SonicFeature.Localizations),
  isFeatureEnabled(SonicFeature.SourceSystemId),
  isFeatureEnabled(SonicFeature.Taxonomies),
);

export const {
  isSuperAdmin,
  isEditor,
  isUsersAdmin,
  isTestSubscriptionsManager,
  isCustomerSupport,
  isAsmTester,
  isTaxonomyManager,
  isSonicStudioBetaTester,
  isSonicDeveloper,
  isCapabilityManager,
  isIpWhitelister,
  isGeoOverrider,
  isCampaignManager,
  isProductManager,
  isLabsManager,
  isLabsViewer,
  isLabsAdmin,
  isLegalManager,
  isTemplateManager,
  isRARApprover,
  isAccountingEditor,
  isContractManager,
  isBusinessReader,
  isEditorialReader,
  isContentPromoter,
  isContentPromoterReader,
  isMerchandiser,
  isMerchandisingReader,
  isMerchandiserManager,
  isProductLineEditor,
  isSiteBuilderManager,
  isSiteConfigManager,
  isStorageManager,
  isFullStoryEnabled,
  isContentInsightEnabled,
  isContentVisualizationEnabled,
  isGeneralRightsEnabled,
  isGenresEnabled,
  isIMSForContentRatingsEnabled,
  isIMSForContentDescriptorsEnabled,
  isIMSForGenresEnabled,
  isIMSForLivestreamsEnabled,
  isIMSForTaxonomyNodesEnabled,
  isIMSForVideoAssetsEnabled,
  isListableAndPlayableAppNamesEnabled,
  isListableAndPlayableTerritoriesEnabled,
  isLiveVideosEnabled,
  isLookerLinksEnabled,
  isMediaDownloadEnabled,
  isOffersEnabled,
  isOffersReadOnlyEnabled,
  isOTPEnabled,
  isMasquerader,
  isPersonalizedAIRecommendationsEnabled,
  isHopperRecommendationsEnabled,
  isProvidersEnabled,
  isRelatedSiteBuilderObjectsEnabled,
  isSourceSystemIdEnabled,
  isSubtitlesEnabled,
  isTagsEnabled,
  isTaxonomiesEnabled,
  isTVListingsEnabled,
  isVideoAssetUploadEnabled,
  isVMSEnabled,
  isMainTerritoryCodeAndBrandIdOnTermKindsEnabled,
  isContentProtectionEnabled,
  isDTCRARContractsEnabled,
  isDTCRARProductsEnabled,
  isPricePlanProvidersWhenOngoingEnabled,
  isMergedCollectionsEnabled,
  isSubscriptionJourneyEnabled,
  isCompassRerankingEnabled,
  isPricePlanFilteringByBrandEnabled,
  isAudienceTargetingEnabled,
  isGauthPartnersEnabled,
  isDetokenizePaymentMethodEnabled,
  isPayPerViewLegacyEnabled,
  isPayPerViewBoltEnabled,
  isTerminateReasonNorwayDenmarkEnabled,
  isPackagesInCapabilitiesEnabled,
  isAddonProductsEnabled,
  canAccessContentInsight,
  canAccessContentVisualization,
  canPlayContent,
  canAccessVideos,
  canManageVideos,
  canAccessVideoProviders,
  canAccessVideoGenres,
  canAccessVideoTagKinds,
  canAccessImageRelationships,
  canManageImageRelationships,
  canAccessSiteBuilder,
  canAccessSiteBuilderBolt,
  canAccessSiteBuilderLegacy,
  canEditPermissions,
  canManageSiteBuilder,
  canEditSiteBuilderConfigs,
  canViewSiteBuilderConfigs,
  canUseBasicAirings,
  canManagePlaylists,
  canUseCompleteAirings,
  canEditSiteBuilderFeatures,
  canViewSiteBuilderFeatures,
  canManageSiteBuilderExperiments,
  canViewSiteBuilderExperiments,
  canUseStudioGlobalExpansionFeatures,
  canViewStudioGlobalExpansionFeatures,
  canUseRolesPermissions,
  canManageMonetization,
  canManagePricePlans,
  canAccessPricePlans,
  canAccessPlans,
  canManagePlans,
  canManageProducts,
  canAccessProducts,
  canAccessSAPMasterData,
  canManageSAPMasterData,
  canManageProductTransitions,
  canAccessProductTransitions,
  canManagePackages,
  canAccessPackages,
  canManageFeatures,
  canAccessFeatures,
  canManageCampaigns,
  canAccessCampaigns,
  canManageAffiliates,
  canAccessAffiliates,
  canManageContracts,
  canAccessContracts,
  canAccessContractsFromProducts,
  canAccessMonetization,
  canAccessMonetizationBolt,
  canAccessMonetizationLegacy,
  canAccessUsers,
  canCreateUser,
  canViewUsers,
  canAccessUserConsents,
  canAccessUserConsentDetails,
  canSearchUsers,
  canDeleteUsers,
  canUpdateUsers,
  hasBlockedEmailPermission,
  hasWhitelistPermission,
  hasGeoOverridePermission,
  canManageTestSubscriptions,
  canDisableMfa,
  canManageUserSubscriptions,
  canManagePPVAccesses,
  canAccessUserRoles,
  canAccessUserCapabilities,
  canAccessUserCapabilitiesV1,
  canAccessUserCapabilitiesV2,
  canAccessUsersCommerceData,
  canSearchAudit,
  canManageFaultyTransactions,
  canAccessCommunicationHistory,
  canManageOffers,
  canAccessOffers,
  canAccessUserIdentity,
  canManageLabsLimited,
  canManageLabsManager,
  canManageLabsFull,
  canAccessLabs,
  canAccessLabsBolt,
  canAccessLabsLegacy,
  canViewLabsData,
  canAccessRatings,
  canManageRatings,
  canAccessTaxonomy,
  canManageTaxonomy,
  canManageTaxonomyDefaultValues,
  canAccessTaxonomyRelationships,
  canEditTaxonomyRelationships,
  canAccessLegal,
  canManageCommunications,
  canAccessCommunications,
  canAccessCapabilities,
  canAccessCapabilitiesBolt,
  canAccessCapabilitiesLegacy,
  canManageCapabilities,
  canAccessRepublishCapabilities,
  canAccessPPVEntitlements,
  canManagePPVEntitlements,
  canAccessPPVEntitlementsBolt,
  canAccessSettings,
  canAccessSiteSetupWizard,
  canAccessSearch,
  canAccessImages,
  canManageSubscriptionJourney,
  canAccessSubscriptionJourney,
  canManageSubscriptionJourneyExperiments,
  canManageMarketingInfo,
  canAccessMarketingInfo,
  canAccessProductVariantsMarketing,
  canAccessUserAddonSubscriptions,
  canAccessAudits,
  canAccessAvatars,
  canAccessPayPerView,
  canManagePayPerView,
  canAccessPayPerViewBolt,
  canManagePayPerViewBolt,
  canDoLogoutEverywhere,
  canToggleReranking,
  canAccessHorizontalReranking,
  canAccessCollectionPersonalization,
  canAccessLegacySearch,
  canAccessVerticalReranking,
  isHotjarEnabled,
  isPostHogEnabled,
  isMicrosoftClarityEnabled,
  canAccessGSM,
  canEditGSM,
  canAccessGSMWarnerBros,
  canAccessGSMDiscoveryGlobal,
  canAccessCollaborators,
  canCreateOrEditRoutes,
  canAccessMultiverse,
  canAccessMultiverseReleaseEnroll,
  isMultiverse,
  canDownloadLegacyStudioUserData,
  canAccessStudioAccess,
  canToggleWholePageOptimization,
  canAccessUserDeviceInfo,
  canAccessTerritoryHierarchyDefinition,
  canAccessDiana,
  canAccessDianaIpRules,
  canViewDianaIpRules,
  canCreateDianaIpRule,
  canAccessDianaIpCategories,
  canAccessGprp,
  canManageGprp,
  canViewStudioRolesWarning,
  canAccessPageGraph,
  canAccessUserDownloadsInfo,
  isRolesPermissionsEnabled,
  canUseMVPDDefederation,
  canAccessBadges,
  canAccessDisclaimers,
  canAccessDisclosures,
  canAccessAdvisories,
  canAccessSegments,
  canUpdateUserMigration,
  canAccessPartnerMapping,
  canManagePartnerMapping,
  isStudioIdentityEnabled,
  canAccessSilhouetteRawProfiles,
  canUpdateSilhouetteProfile,
  canAccessProfileSimulator,
  canViewAuthProvidersInfo,
  canAccessElvisLegalFeatureFlag,
  canAccessFlash,
  canAccessSpeed,
  canAccess$AppName,
  canViewAuthProvidersVerificationStatus,
  canAccessMosaic,
  isMosaicDataEngineer,
  canAccessCRM,
  canEditCRMActivation,
  canEditAudienceActivation,
  canAccessAudienceActivation,
  canAccessSiteReporting,
  canAccessBolt,
  canAccessMemberHub,
  isUserSubscriptionsBoltEnabled,
  canEnforceUserPasswordReset,
  canAccessStudioSandbox,
  isMultiMarketCampaignsEnabled,

  /* Badge Management */
  isBadgeManager,

  /* Content Promotion */
  isContentPromotionEnabled,
  isContentPromotionHopperEnabled,
  isContentPromotionCampaignsEnabled,
  canUseGlobalHopper,
  canAccessContentPromotion,
  canAccessContentPromotionDplusApi,
  canAccessImmersiveHero,
  canAccessDistributionChannelCandidates,
  canAccessTaxonomyLinkCandidates,
  canManageHopperSettings,

  /* Curation Promotion */
  isCurationPromotionEnabled,
  isCurationPromotionSettingsEnabled,
  canAccessCurationPromotion,
  canAccessCurationPromotionSettings,
  canAccessCurationPromotionOverrides,
  canAccessCurationPromotionCampaigns,

  /** Partner Portal */
  canAccessPartnerPortal,
  canUsePartnerPortalExtendedCreateForm,
  canManagePartnerPortal,

  /** Settings */
  isPublishingSitesHardcoded,

  /** Localization */
  isLocalizationsEnabled,
  isBoltLocalizationEnabled,
  isLinkedBadgesAndLegalLocalizationsEnabled,
  canAccessLocalizationReporting,
  canAccessLocalizationBulkUpload,
  canLocalizeContentRestrictions,
  canManageBadgeLocalizations,
  canManageDisclaimerLocalizations,
  canManageAdvisoryLocalizations,
  canManageDisclosureLocalizations,

  /** Multiconfig */
  isMulticonfigEnabled,
  isTerritoriesFromMultiConfigEnabled,
  isTerritoryGroupsFromMultiConfigEnabled,

  canAccessBulkRefund,
  canAccessUserPreferences,
  canAccessPlanBulkActions,
  canAccessMulticonfig,
  canAccessPersonalizedOffers,
  canManagePersonalizedOffers,
  canCopyMarketingPages,
  canAccessServiceCatalog,
  canAccessStudioSupportTeamsManagement,
  canAccessStudioTeams,
  canAccessStudioStorage,
  canAccessStudioStorageAdmin,
  canMasquerade,
  canMasqueradeAllRoles,
  canAccessPersonalizationSandbox,
  canAccessStudioChatBot,
  canAccessStudioQuery,
  canAccessMessaging,
  canAccessSNOWRequests,
  canAccessCapabilityValidator,
  canAccessStaticLocalizations,
  canAccessCxTooling,
  canEditMessagingCampaigns,
  canAccessBlockEmailDomain,
  isAuditLogBetaEnabled,
  canAccessAuditLogBeta,
  // 🚨 DON'T REMOVE COMMENT : Export your access above
} = ruleEvaluators;
